import {
  Check,
  ChevronDown,
  CreditCard,
  ExternalLink,
  Plus
} from 'lucide-react';
import type {
  ListAddAction,
  ListAddTemplate
} from 'v5/core/form-schema/formSchema.types';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';

export interface ListAddButtonProps {
  add: ListAddAction;
  /** `null` adds a blank row; otherwise the id of the picked template. */
  onAdd(optionId: string | null): void;
  /**
   * Templates already in the list (a linked rate card): still listed, so
   * people can see why they cannot pick them again, but marked "Added".
   */
  usedOptions?: ReadonlyArray<string>;
  disabled?: boolean;
}

const NO_USED_OPTIONS: ReadonlyArray<string> = [];

/**
 * "Add item" control of a list unit: one button adding a blank row, and for
 * lists with templates a picker beside it ("Add rate" + "From rate card").
 */
export function ListAddButton({
  add,
  onAdd,
  usedOptions = NO_USED_OPTIONS,
  disabled = false
}: ListAddButtonProps) {
  const addBlank = (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => onAdd(null)}
      disabled={disabled}
      data-testid="list-add"
    >
      <Plus className="size-4" />
      {add.label}
    </Button>
  );
  if (add.type === 'single') return addBlank;

  const { templates } = add;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {addBlank}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            data-testid="list-add-template"
          >
            <CreditCard className="size-4" />
            {templates.label}
            <ChevronDown className="size-4 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-80 w-80 max-w-[calc(100vw-2rem)] overflow-y-auto"
        >
          <ListAddTemplateItems
            options={templates.options}
            usedOptions={usedOptions}
            onAdd={onAdd}
          />
          <ListAddManageItem
            manage={add.manage}
            separated={templates.options.length > 0}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Below the templates, after a line: where they are kept, opened in a new
 * tab (unsaved edits here stay), for one that isn't listed.
 */
export function ListAddManageItem({
  manage,
  separated = true
}: {
  manage: { label: string; href: string } | null;
  /** A line above it, after the items before it; none when it is the menu's only item. */
  separated?: boolean;
}) {
  if (manage === null) return null;
  return (
    <>
      {separated && <DropdownMenuSeparator />}
      <DropdownMenuItem asChild className="gap-3 py-2 text-fg-muted">
        <a href={manage.href} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="size-4" aria-hidden="true" />
          {manage.label}
        </a>
      </DropdownMenuItem>
    </>
  );
}

/** A list's templates as menu items (a rate card each); those already in the list are marked "Added". */
export function ListAddTemplateItems({
  options,
  usedOptions = NO_USED_OPTIONS,
  onAdd
}: {
  options: ReadonlyArray<ListAddTemplate>;
  usedOptions?: ReadonlyArray<string>;
  onAdd(optionId: string): void;
}) {
  return options.map(option => {
    const used = usedOptions.includes(option.id);
    return (
      <DropdownMenuItem
        key={option.id}
        disabled={used}
        onSelect={() => onAdd(option.id)}
        data-testid={`list-add-option-${option.id}`}
        className="gap-3 py-2"
      >
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-semibold text-fg-default">
            {option.label}
          </span>
          {option.description !== null && (
            <span className="text-sm text-pretty text-fg-muted tabular-nums">
              {option.description}
            </span>
          )}
        </span>
        {used && (
          <span className="flex shrink-0 items-center gap-1 text-sm text-fg-muted">
            <Check className="size-4" aria-hidden="true" />
            Added
          </span>
        )}
      </DropdownMenuItem>
    );
  });
}
